import React from 'react';
import HelpBadge from './components/HelpBadge';
import { ResearchPersonIntake, type ResearchPersonKeyData } from './ResearchPersonIntakeForm';
import { ResearchPhoneList } from './ResearchPhoneList';
import {
  researchIntakeClearedPersonFields,
  researchIntakeCurrentStage,
  researchIntakeFollowupSections,
  researchIntakeSteps,
  researchIntakeSubjectNameStarted,
  type ResearchIntakeKind,
} from './researchIntakeWorkflow';
import type { OfacLanguage } from './ofacIntakeCatalog';
import {
  OFAC_FAMILY_RELATIONS,
  OFAC_INTAKE_MAX_ROWS,
  appendOfacIntakeRow,
  emptyOfacIntakeFamilyMember,
  patchOfacIntakeRow,
  removeOfacIntakeRow,
  type OfacIntakeFamilyMember,
} from './ofacNamedWhitelistUi';

export type ResearchKeyDataIntakeFields = ResearchPersonKeyData & {
  website: string;
  phones: string;
  emails: string;
  companies: string;
  socialMediaHandles: string;
  knownAliases: string;
  familyMembers?: OfacIntakeFamilyMember[];
};

type OfacRoleOption = {
  value: string;
  labelEn: string;
  labelEs: string;
};

type ResearchKeyDataIntakeProps<T extends ResearchKeyDataIntakeFields> = {
  language: OfacLanguage;
  targetType: ResearchIntakeKind;
  targetTypeTouched: boolean;
  keyData: T;
  reportName: string;
  affinityDepth: number;
  onAffinityDepthChange: (depth: 1 | 2 | 3 | 4) => void;
  canAccessOfac: boolean;
  addToOfacScreening: boolean;
  ofacRelationshipType: string;
  ofacRelationshipOptions: OfacRoleOption[];
  fieldProps: (field: string) => Record<string, unknown>;
  intakeEpoch: number;
  uploadFile: File | null;
  onChooseType: (kind: ResearchIntakeKind) => void;
  onKeyDataChange: (next: T) => void;
  onReportNameChange: (value: string) => void;
  onOfacChange: (patch: { addToOfacScreening?: boolean; ofacRelationshipType?: string }) => void;
  onUploadFile: (file: File | null) => void;
};

export function ResearchKeyDataIntake<T extends ResearchKeyDataIntakeFields>({
  language,
  targetType,
  targetTypeTouched,
  keyData,
  reportName,
  affinityDepth,
  onAffinityDepthChange,
  canAccessOfac,
  addToOfacScreening,
  ofacRelationshipType,
  ofacRelationshipOptions,
  fieldProps,
  intakeEpoch,
  uploadFile,
  onChooseType,
  onKeyDataChange,
  onReportNameChange,
  onOfacChange,
  onUploadFile,
}: ResearchKeyDataIntakeProps<T>) {
  const [followupOpen, setFollowupOpen] = React.useState(false);
  const kind = targetTypeTouched ? targetType : '';
  const steps = researchIntakeSteps(kind);
  const currentStage = researchIntakeCurrentStage(kind, followupOpen);
  const followupSections = kind ? researchIntakeFollowupSections(kind) : [];
  const showReportName = researchIntakeSubjectNameStarted(kind, keyData);
  const spanish = language === 'es';

  React.useEffect(() => {
    setFollowupOpen(false);
  }, [kind]);

  const patchKey = (partial: Partial<T>) => onKeyDataChange({ ...keyData, ...partial });

  const contactFields = (
    <div className="research-intake-section">
      <p className="research-form-panel-help">
        {spanish
          ? 'Identifican al sujeto. Las direcciones de entrega del PDF están en Configuración de búsqueda (engranaje).'
          : 'These identify the subject. PDF delivery emails are in Search settings (gear).'}
      </p>
      {kind === 'company' ? (
        <label className="research-form-panel-label">
          <span>{spanish ? 'Direcciones / ubicaciones' : 'Addresses / locations'}</span>
          <textarea
            {...fieldProps('addresses')}
            placeholder={spanish ? 'Separadas por coma' : 'Comma separated'}
            value={keyData.addresses}
            onChange={(event) => patchKey({ addresses: event.target.value } as Partial<T>)}
          />
        </label>
      ) : null}
      <div className="research-intake-pair">
        <ResearchPhoneList
          language={language}
          value={keyData.phones || ''}
          onChange={(phones) => patchKey({ phones } as Partial<T>)}
          fieldProps={fieldProps}
        />
        <div className="research-form-panel-label">
          <label htmlFor={`nc-research-emails-${intakeEpoch}`}>
            {spanish ? 'Correo del sujeto' : 'Subject email'}
          </label>
          <textarea
            aria-describedby="research-subject-emails-hint"
            {...fieldProps('emails')}
            placeholder={spanish ? 'nombre@empresa.com' : 'name@company.com'}
            value={keyData.emails || ''}
            onChange={(event) => patchKey({ emails: event.target.value } as Partial<T>)}
          />
          <span id="research-subject-emails-hint" className="research-form-panel-meta">
            {spanish
              ? 'Separados por coma. Sirven para encontrar a la persona o empresa, no para enviar el PDF.'
              : 'Comma-separated. Used to find the person or company — not where the PDF is sent.'}
          </span>
        </div>
      </div>
    </div>
  );

  const moreFields = (
    <div className="research-intake-section">
      {kind === 'company' ? (
        <label className="research-form-panel-label">
          <span>{spanish ? 'Alias conocidos' : 'Known aliases'}</span>
          <textarea
            {...fieldProps('knownAliases')}
            placeholder={spanish ? 'Separados por coma' : 'Comma separated'}
            value={keyData.knownAliases}
            onChange={(event) => patchKey({ knownAliases: event.target.value } as Partial<T>)}
          />
        </label>
      ) : null}
      {kind === 'person' ? (
        <>
          <label className="research-form-panel-label">
            <span>{spanish ? 'Compañías / afiliaciones' : 'Companies / affiliations'}</span>
            <textarea
              {...fieldProps('companies')}
              placeholder={spanish ? 'Separadas por coma' : 'Comma separated'}
              value={keyData.companies}
              onChange={(event) => patchKey({ companies: event.target.value } as Partial<T>)}
            />
          </label>
          <div className="research-intake-section">
            <h4 className="research-intake-section-title">
              {spanish ? 'Familiares conocidos' : 'Known family members'}
            </h4>
            <p className="research-form-panel-help">
              {spanish
                ? 'Nombre legal y tipo de relación (cónyuge, padre/madre, hermano/a, hijo/a, asociado). Sirven como semillas de búsqueda; no se tratan como coincidencias confirmadas.'
                : 'Legal name and relationship type (spouse, parent, sibling, child, associate). Used as search seeds — not assumed matches.'}
            </p>
            <div className="research-intake-repeat">
              {(keyData.familyMembers?.length ? keyData.familyMembers : [emptyOfacIntakeFamilyMember()]).map((row, index) => (
                <div className="research-intake-pair" key={`research-family-${index}`}>
                  <label className="research-form-panel-label">
                    <span>{spanish ? 'Relación' : 'Relation'}</span>
                    <select
                      value={row.relation}
                      onChange={(event) => patchKey({
                        familyMembers: patchOfacIntakeRow(
                          keyData.familyMembers,
                          index,
                          { relation: event.target.value },
                          emptyOfacIntakeFamilyMember(),
                        ),
                      } as Partial<T>)}
                    >
                      {OFAC_FAMILY_RELATIONS.map((relation) => (
                        <option key={relation.value} value={relation.value}>
                          {spanish ? relation.labelEs : relation.labelEn}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="research-form-panel-label">
                    <span>{spanish ? 'Nombre legal' : 'Legal name'}</span>
                    <input
                      type="text"
                      {...fieldProps(`familyMemberName-${index}`)}
                      value={row.name}
                      placeholder={spanish ? 'Nombre legal' : 'Legal name'}
                      onChange={(event) => patchKey({
                        familyMembers: patchOfacIntakeRow(
                          keyData.familyMembers,
                          index,
                          { name: event.target.value },
                          emptyOfacIntakeFamilyMember(),
                        ),
                      } as Partial<T>)}
                    />
                  </label>
                  {(keyData.familyMembers?.length || 0) > 1 ? (
                    <button
                      type="button"
                      className="dashboard-security-inline-btn"
                      onClick={() => patchKey({
                        familyMembers: removeOfacIntakeRow(
                          keyData.familyMembers,
                          index,
                          emptyOfacIntakeFamilyMember(),
                        ),
                      } as Partial<T>)}
                    >
                      {spanish ? 'Quitar' : 'Remove'}
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="dashboard-security-inline-btn"
                disabled={(keyData.familyMembers?.length || 0) >= OFAC_INTAKE_MAX_ROWS}
                onClick={() => patchKey({
                  familyMembers: appendOfacIntakeRow(
                    keyData.familyMembers,
                    emptyOfacIntakeFamilyMember(),
                  ),
                } as Partial<T>)}
              >
                {spanish ? 'Añadir otro familiar' : 'Add another family member'}
              </button>
            </div>
          </div>
        </>
      ) : null}
      <label className="research-form-panel-label">
        <span>{spanish ? 'Cuentas de redes sociales' : 'Social media handles'}</span>
        <textarea
          {...fieldProps('socialMediaHandles')}
          placeholder={spanish ? 'Separados por coma' : 'Comma separated'}
          value={keyData.socialMediaHandles}
          onChange={(event) => patchKey({ socialMediaHandles: event.target.value } as Partial<T>)}
        />
      </label>
      <label className="research-form-panel-label">
        <span>{spanish ? 'Adjuntar documento (PDF o DOCX, opcional)' : 'Attach document (PDF or DOCX, optional)'}</span>
        <input
          type="file"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(event) => onUploadFile(event.target.files?.[0] ?? null)}
          style={{ fontSize: '0.88rem' }}
        />
        {uploadFile ? (
          <span className="research-form-panel-meta">
            {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
          </span>
        ) : null}
      </label>
      {canAccessOfac ? (
        <div className="research-form-panel-label" style={{ borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
          <label className="dashboard-checkbox-row" style={{ alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={Boolean(addToOfacScreening)}
              onChange={(event) => onOfacChange({ addToOfacScreening: event.target.checked })}
            />
            <span>
              <strong>{spanish ? 'Añadir a la lista de Screening OFAC' : 'Add to OFAC Screening list'}</strong>
              <span className="research-form-panel-meta" style={{ display: 'block', marginTop: '4px' }}>
                {spanish
                  ? 'Si se marca, este sujeto se une a la cartera OFAC al enviar.'
                  : 'When checked, this subject joins the OFAC portfolio on submit.'}
              </span>
            </span>
          </label>
          {addToOfacScreening ? (
            <label className="research-form-panel-label" style={{ marginTop: '8px' }}>
              <span>{spanish ? 'Rol / relación OFAC' : 'OFAC role / relationship'}</span>
              <select
                value={ofacRelationshipType}
                onChange={(event) => onOfacChange({ ofacRelationshipType: event.target.value })}
              >
                {ofacRelationshipOptions.map((option) => (
                  <option key={`intake-ofac-${option.value}`} value={option.value}>
                    {spanish ? option.labelEs : option.labelEn}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="research-intake-flow">
      {steps.length > 0 ? (
        <ol className="research-intake-steps" aria-label={spanish ? 'Pasos de entrada' : 'Intake steps'}>
          {steps.map((step) => (
            <li
              key={step.id}
              className={currentStage === step.id ? 'is-current' : undefined}
              aria-current={currentStage === step.id ? 'step' : undefined}
            >
              {spanish ? step.labelEs : step.labelEn}
              {step.optional ? ` (${spanish ? 'opcional' : 'optional'})` : ''}
            </li>
          ))}
        </ol>
      ) : null}

      <div className="research-intake-start-row">
        <fieldset className="research-intake-target-type">
          <legend>{spanish ? '¿Persona o empresa?' : 'Person or company?'}</legend>
          <label>
            <input
              type="radio"
              name="research-target-type"
              checked={kind === 'person'}
              onChange={() => onChooseType('person')}
            />
            {spanish ? 'Persona' : 'Person'}
          </label>
          <label>
            <input
              type="radio"
              name="research-target-type"
              checked={kind === 'company'}
              onChange={() => onChooseType('company')}
            />
            {spanish ? 'Empresa / sitio web' : 'Company / website'}
          </label>
        </fieldset>
        <div className="research-intake-affinity">
          <label htmlFor="research-intake-affinity-depth">
            {spanish ? 'Afinidad' : 'Affinity'}
          </label>
          <HelpBadge
            label={spanish ? 'Ayuda sobre el nivel de afinidad' : 'Help about affinity level'}
          >
            {spanish
              ? 'Nivel 1 usa entidades del reporte raíz. Nivel 2+ ejecuta una búsqueda completa por cada persona/empresa descubierta en el nivel anterior y amplía el grafo con esos resultados.'
              : 'Level 1 maps entities from the root report. Level 2+ runs a full follow-up search for each person/company found at the prior level, then expands the graph with those results.'}
          </HelpBadge>
          <select
            id="research-intake-affinity-depth"
            className="research-intake-affinity-select"
            value={Math.max(1, Math.min(4, Number(affinityDepth) || 1))}
            onChange={(event) => {
              const parsed = Number(event.target.value);
              onAffinityDepthChange((Number.isFinite(parsed) ? Math.max(1, Math.min(4, parsed)) : 1) as 1 | 2 | 3 | 4);
            }}
            aria-label={spanish ? 'Nivel de afinidad' : 'Affinity Level'}
          >
            {[1, 2, 3, 4].map((depth) => (
              <option key={`research-intake-affinity-${depth}`} value={depth}>
                {depth}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!kind ? (
        <p className="research-form-panel-help">
          {spanish
            ? 'Elige una opción. Luego aparece el nombre legal actual. Los nombres anteriores y la fecha de nacimiento o el rango de edad van con la identidad; los IDs extra se abren sólo si los necesitas.'
            : 'Choose person or company. The current legal name appears next. Former names and date of birth or age range stay with identity; extra IDs stay closed until you need them.'}
        </p>
      ) : null}

      {kind === 'person' ? (
        <ResearchPersonIntake
          language={language}
          keyData={keyData}
          onKeyDataChange={onKeyDataChange}
          fieldProps={fieldProps}
          sections={['identity']}
        />
      ) : null}

      {kind === 'company' ? (
        <div className="research-intake-section">
          <h4 className="research-intake-section-title">
            {spanish ? 'Empresa' : 'Company'}
          </h4>
          <label className="research-form-panel-label">
            <span>{spanish ? 'Nombre legal' : 'Legal name'}</span>
            <textarea
              {...fieldProps('names')}
              placeholder={spanish ? 'Separados por coma' : 'Comma separated'}
              value={keyData.names}
              onChange={(event) => patchKey({ names: event.target.value } as Partial<T>)}
            />
          </label>
          <label className="research-form-panel-label">
            <span>{spanish ? 'Sitio web' : 'Website'}</span>
            <input
              type="text"
              inputMode="url"
              {...fieldProps('website')}
              placeholder={spanish ? 'ejemplo.com (sin https)' : 'example.com (https optional)'}
              value={keyData.website}
              onChange={(event) => patchKey({ website: event.target.value } as Partial<T>)}
            />
          </label>
        </div>
      ) : null}

      {showReportName ? (
        <label className="research-form-panel-label research-form-panel-label--untitled research-intake-report-name">
          <span className="visually-hidden">{spanish ? 'Nombre del reporte' : 'Report name'}</span>
          <input
            type="text"
            {...fieldProps('reportName')}
            placeholder={spanish ? 'Nombre del reporte' : 'Report Name'}
            value={reportName}
            onChange={(event) => onReportNameChange(event.target.value)}
          />
        </label>
      ) : null}

      {kind && !followupOpen ? (
        <button
          type="button"
          className="dashboard-security-inline-btn research-intake-continue"
          onClick={() => setFollowupOpen(true)}
        >
          {kind === 'person'
            ? (spanish ? 'Añadir IDs, contacto y más' : 'Add IDs, contact, and more')
            : (spanish ? 'Añadir contacto y más' : 'Add contact and more')}
        </button>
      ) : null}

      {kind && followupOpen ? (
        <div className="research-intake-followup">
          {followupSections.includes('identifiers') ? (
            <details className="research-intake-stage">
              <summary>{spanish ? 'Ubicación (opcional)' : 'Location (optional)'}</summary>
              <ResearchPersonIntake
                language={language}
                keyData={keyData}
                onKeyDataChange={onKeyDataChange}
                fieldProps={fieldProps}
                sections={['identifiers']}
              />
            </details>
          ) : null}
          {followupSections.includes('contact') ? (
            <details className="research-intake-stage">
              <summary>{spanish ? 'Contacto (opcional)' : 'Contact (optional)'}</summary>
              {contactFields}
            </details>
          ) : null}
          {followupSections.includes('more') ? (
            <details className="research-intake-stage">
              <summary>{spanish ? 'Más datos (opcional)' : 'More details (optional)'}</summary>
              {moreFields}
            </details>
          ) : null}
          <button
            type="button"
            className="dashboard-security-inline-btn"
            onClick={() => setFollowupOpen(false)}
          >
            {spanish ? 'Ocultar datos extra' : 'Hide extra details'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function chooseResearchIntakeKind<T extends ResearchKeyDataIntakeFields>(
  current: { keyData: T; ofacRelationshipType?: string },
  kind: ResearchIntakeKind,
): { targetType: ResearchIntakeKind; targetTypeTouched: true; ofacRelationshipType: string; keyData: T } {
  if (kind === 'company') {
    return {
      targetType: 'company',
      targetTypeTouched: true,
      ofacRelationshipType: 'customer',
      keyData: researchIntakeClearedPersonFields(current.keyData),
    };
  }
  return {
    targetType: 'person',
    targetTypeTouched: true,
    ofacRelationshipType: 'contractor',
    keyData: { ...current.keyData },
  };
}
